import { useEffect, useState } from 'react'
import nexusApi from '../../services/nexusApi'
import { LifeBuoy, Plus, MessageSquare, Clock, CheckCircle, AlertTriangle, ChevronDown, X, Send } from 'lucide-react'

const NX = {
  bg: '#0D1117', surface: '#161B22', surface2: '#1C2330', border: '#2A3441',
  text: '#E6EDF3', textDim: '#8B949E', accent: '#0066FF',
  green: '#3DB54A', amber: '#F5921E', red: '#EF4444',
}

const PRIORITY_COLORS: Record<string, string> = { p1: NX.red, p2: NX.amber, p3: NX.accent, p4: NX.textDim }
const PRIORITY_LABELS: Record<string, string> = { p1: 'P1 Critical', p2: 'P2 High', p3: 'P3 Normal', p4: 'P4 Low' }
const STATUS_COLORS: Record<string, string> = { open: NX.amber, in_progress: NX.accent, resolved: NX.green, closed: NX.textDim }

function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span style={{
      background: `${color}22`, color, fontSize: '0.6875rem', fontWeight: 700,
      padding: '2px 7px', borderRadius: 99, textTransform: 'uppercase', letterSpacing: '0.04em',
    }}>
      {label}
    </span>
  )
}

function timeAgo(date: string) {
  const diff = Date.now() - new Date(date).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

export default function NexusSupport() {
  const [tickets, setTickets] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<any>(null)
  const [messages, setMessages] = useState<any[]>([])
  const [replyText, setReplyText] = useState('')
  const [sending, setSending] = useState(false)
  const [filter, setFilter] = useState('all')
  const [showCreate, setShowCreate] = useState(false)
  const [newTicket, setNewTicket] = useState({ subject: '', body: '', priority: 'p3', tenant_id: '' })
  const [tenants, setTenants] = useState<any[]>([])

  const load = async () => {
    setLoading(true)
    try {
      const params = filter !== 'all' ? `?status=${filter}` : ''
      const { data } = await nexusApi.get(`/support/tickets${params}`)
      setTickets(data.tickets || [])
      setTotal(data.total || 0)
    } catch {}
    setLoading(false)
  }

  useEffect(() => { load() }, [filter])

  useEffect(() => {
    nexusApi.get('/tenants?limit=100').then(r => setTenants(r.data.tenants || [])).catch(() => {})
  }, [])

  const loadTicket = async (ticket: any) => {
    setSelected(ticket)
    try {
      const { data } = await nexusApi.get(`/support/tickets/${ticket.id}`)
      setMessages(data.messages || [])
    } catch {}
  }

  const sendReply = async () => {
    if (!replyText.trim() || !selected) return
    setSending(true)
    try {
      await nexusApi.post(`/support/tickets/${selected.id}/reply`, { message: replyText })
      setReplyText('')
      await loadTicket(selected)
      load()
    } catch {}
    setSending(false)
  }

  const resolveTicket = async () => {
    if (!selected) return
    try {
      await nexusApi.post(`/support/tickets/${selected.id}/resolve`)
      setSelected(null)
      load()
    } catch {}
  }

  const createTicket = async () => {
    if (!newTicket.subject || !newTicket.body) return
    try {
      await nexusApi.post('/support/tickets', newTicket)
      setShowCreate(false)
      setNewTicket({ subject: '', body: '', priority: 'p3', tenant_id: '' })
      load()
    } catch {}
  }

  const CANNED = [
    'Thank you for reaching out. We\'re investigating this issue and will update you shortly.',
    'Could you please provide more details about the steps to reproduce this issue?',
    'This has been resolved. Please let us know if you experience any further issues.',
    'We\'ve escalated this to our development team for investigation.',
  ]

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 0px)', overflow: 'hidden' }}>
      {/* Ticket list */}
      <div style={{
        width: selected ? 380 : '100%', borderRight: selected ? `1px solid ${NX.border}` : 'none',
        display: 'flex', flexDirection: 'column', background: NX.bg, overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '1.5rem 1.5rem 1rem', borderBottom: `1px solid ${NX.border}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.875rem' }}>
            <div>
              <h1 style={{ color: NX.text, fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Support</h1>
              <p style={{ color: NX.textDim, fontSize: '0.8125rem', margin: '0.125rem 0 0' }}>{total} tickets</p>
            </div>
            <button onClick={() => setShowCreate(true)} style={{
              display: 'flex', alignItems: 'center', gap: 6, background: NX.accent,
              color: '#fff', border: 'none', borderRadius: 8, padding: '0.5rem 0.875rem',
              fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer',
            }}>
              <Plus size={14} /> New Ticket
            </button>
          </div>
          {/* Filter chips */}
          <div style={{ display: 'flex', gap: 6 }}>
            {['all', 'open', 'in_progress', 'resolved'].map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{
                background: filter === f ? `${NX.accent}22` : 'transparent',
                border: `1px solid ${filter === f ? NX.accent : NX.border}`,
                color: filter === f ? NX.accent : NX.textDim,
                borderRadius: 99, padding: '3px 10px', fontSize: '0.75rem', fontWeight: 600,
                cursor: 'pointer', textTransform: 'capitalize',
              }}>
                {f === 'all' ? 'All' : f.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Ticket list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ color: NX.textDim, fontSize: '0.875rem', textAlign: 'center', padding: '3rem 0' }}>Loading…</div>
          ) : tickets.length === 0 ? (
            <div style={{ color: NX.textDim, fontSize: '0.875rem', textAlign: 'center', padding: '3rem 0' }}>
              <LifeBuoy size={32} color={NX.border} style={{ display: 'block', margin: '0 auto 8px' }} />
              No tickets
            </div>
          ) : tickets.map(t => (
            <div
              key={t.id}
              onClick={() => loadTicket(t)}
              style={{
                padding: '0.875rem 1.25rem',
                borderBottom: `1px solid ${NX.border}`,
                cursor: 'pointer',
                background: selected?.id === t.id ? NX.surface2 : 'transparent',
                transition: 'background 0.1s',
                borderLeft: `3px solid ${t.priority === 'p1' ? NX.red : t.priority === 'p2' ? NX.amber : 'transparent'}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                <div style={{ color: NX.text, fontSize: '0.875rem', fontWeight: 500, flex: 1 }}>{t.subject}</div>
                <div style={{ flexShrink: 0, display: 'flex', gap: 4 }}>
                  <Badge label={PRIORITY_LABELS[t.priority] || t.priority} color={PRIORITY_COLORS[t.priority] || NX.textDim} />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Badge label={t.status.replace('_', ' ')} color={STATUS_COLORS[t.status] || NX.textDim} />
                {t.tenant_name && <span style={{ color: NX.textDim, fontSize: '0.75rem' }}>{t.tenant_name}</span>}
                <span style={{ color: NX.textDim, fontSize: '0.6875rem', marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Clock size={10} /> {timeAgo(t.created_at)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Ticket detail */}
      {selected && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: NX.bg, overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${NX.border}`, flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <h2 style={{ color: NX.text, fontSize: '1rem', fontWeight: 700, margin: '0 0 6px' }}>{selected.subject}</h2>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Badge label={PRIORITY_LABELS[selected.priority]} color={PRIORITY_COLORS[selected.priority]} />
                  <Badge label={selected.status.replace('_', ' ')} color={STATUS_COLORS[selected.status]} />
                  {selected.tenant_name && <span style={{ color: NX.textDim, fontSize: '0.75rem' }}>Tenant: {selected.tenant_name}</span>}
                  {selected.user_email && <span style={{ color: NX.textDim, fontSize: '0.75rem' }}>User: {selected.user_email}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                {selected.status !== 'resolved' && selected.status !== 'closed' && (
                  <button onClick={resolveTicket} style={{
                    display: 'flex', alignItems: 'center', gap: 6, background: `${NX.green}22`,
                    color: NX.green, border: `1px solid ${NX.green}44`, borderRadius: 8,
                    padding: '0.4rem 0.75rem', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer',
                  }}>
                    <CheckCircle size={13} /> Resolve
                  </button>
                )}
                <button onClick={() => setSelected(null)} style={{
                  background: 'none', border: 'none', cursor: 'pointer', color: NX.textDim, padding: 4,
                }}>
                  <X size={18} />
                </button>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.5rem', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Original message */}
            <div style={{ background: NX.surface, borderRadius: 10, padding: '0.875rem', border: `1px solid ${NX.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: NX.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, color: '#fff' }}>
                  {selected.user_email?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <span style={{ color: NX.text, fontSize: '0.8125rem', fontWeight: 600 }}>{selected.user_email || 'User'}</span>
                <span style={{ color: NX.textDim, fontSize: '0.6875rem' }}>{timeAgo(selected.created_at)}</span>
              </div>
              <div style={{ color: NX.textDim, fontSize: '0.875rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{selected.body}</div>
            </div>

            {messages.map(msg => (
              <div key={msg.id} style={{
                background: msg.sender_type === 'super_admin' ? `${NX.accent}15` : NX.surface,
                borderRadius: 10, padding: '0.875rem',
                border: `1px solid ${msg.sender_type === 'super_admin' ? `${NX.accent}33` : NX.border}`,
                marginLeft: msg.sender_type === 'super_admin' ? '2rem' : 0,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ color: NX.text, fontSize: '0.8125rem', fontWeight: 600 }}>
                    {msg.sender_type === 'super_admin' ? '🛡️ Support Team' : 'User'}
                  </span>
                  <span style={{ color: NX.textDim, fontSize: '0.6875rem' }}>{timeAgo(msg.created_at)}</span>
                </div>
                <div style={{ color: NX.textDim, fontSize: '0.875rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{msg.message}</div>
              </div>
            ))}
          </div>

          {/* Canned responses */}
          <div style={{ padding: '0.5rem 1.5rem 0', flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {CANNED.map((c, i) => (
                <button key={i} onClick={() => setReplyText(c)} style={{
                  background: NX.surface, border: `1px solid ${NX.border}`, color: NX.textDim,
                  borderRadius: 6, padding: '3px 8px', fontSize: '0.6875rem', cursor: 'pointer',
                }}>
                  {c.slice(0, 30)}…
                </button>
              ))}
            </div>
          </div>

          {/* Reply box */}
          <div style={{ padding: '0.875rem 1.5rem', borderTop: `1px solid ${NX.border}`, flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: 10 }}>
              <textarea
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                placeholder="Type a reply…"
                rows={2}
                style={{
                  flex: 1, background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 8,
                  color: NX.text, fontSize: '0.875rem', padding: '0.625rem 0.75rem', resize: 'none',
                  outline: 'none',
                }}
              />
              <button onClick={sendReply} disabled={sending || !replyText.trim()} style={{
                background: NX.accent, color: '#fff', border: 'none', borderRadius: 8,
                padding: '0 1rem', cursor: replyText.trim() ? 'pointer' : 'default',
                opacity: replyText.trim() ? 1 : 0.5, display: 'flex', alignItems: 'center',
              }}>
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create ticket modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: NX.surface, borderRadius: 16, border: `1px solid ${NX.border}`, width: '100%', maxWidth: 480, padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
              <h2 style={{ color: NX.text, fontWeight: 700, margin: 0 }}>New Support Ticket</h2>
              <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: NX.textDim }}><X size={18} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <select value={newTicket.tenant_id} onChange={e => setNewTicket(t => ({ ...t, tenant_id: e.target.value }))}
                style={{ background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, color: NX.text, padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}>
                <option value="">— Select tenant (optional) —</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <input value={newTicket.subject} onChange={e => setNewTicket(t => ({ ...t, subject: e.target.value }))}
                placeholder="Subject"
                style={{ background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, color: NX.text, padding: '0.5rem 0.75rem', fontSize: '0.875rem' }} />
              <textarea value={newTicket.body} onChange={e => setNewTicket(t => ({ ...t, body: e.target.value }))}
                placeholder="Describe the issue…" rows={4}
                style={{ background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, color: NX.text, padding: '0.5rem 0.75rem', fontSize: '0.875rem', resize: 'vertical' }} />
              <select value={newTicket.priority} onChange={e => setNewTicket(t => ({ ...t, priority: e.target.value }))}
                style={{ background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, color: NX.text, padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}>
                <option value="p1">P1 — Critical</option>
                <option value="p2">P2 — High</option>
                <option value="p3">P3 — Normal</option>
                <option value="p4">P4 — Low</option>
              </select>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowCreate(false)} style={{ background: NX.surface2, border: `1px solid ${NX.border}`, color: NX.textDim, borderRadius: 8, padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.875rem' }}>Cancel</button>
                <button onClick={createTicket} style={{ background: NX.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '0.5rem 1.25rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>Create</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
